import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import ResourceCard from "../learner/ResourceCard";
import {useCart} from "../CardContext";
import {CART_NOTIFY_EVENT} from "./useLandingCart";
import {useUserPublicCatalog} from "../../store/user";
import {
  MATERIAL_TYPES,
  mapPublicResource,
  materialDetailPath,
  materialTypeLabel,
} from "./publicMaterialMap";

const TAB_LABELS = {
  books: "Books",
  journals: "Journals",
  "seminar-topics": "Seminars",
  "project-topics": "Projects",
  "research-materials": "Research materials",
  manuals: "Manuals",
};

const TABS = [{id: "", label: "All"}, ...MATERIAL_TYPES.map((id) => ({id, label: TAB_LABELS[id]}))];

const cartMaterialType = (type) => {
  if (type === "books") return "book";
  if (type === "journals") return "journal";
  return type;
};

const sameCartType = (left, right) => String(left || "")
  .toLowerCase()
  .replace(/_/g, "-")
  .replace(/s$/, "") === String(right || "")
  .toLowerCase()
  .replace(/_/g, "-")
  .replace(/s$/, "");

function CardGridSkeleton({count = 4}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({length: count}).map((_, index) => (
        <div
          key={index}
          className="h-72 animate-pulse rounded-md border border-slate-200 bg-slate-100"
        />
      ))}
    </div>
  );
}

/**
 * API-backed marketplace materials section (books, journals, seminars,
 * projects, research materials, manuals).
 *
 *   GET /api/public/catalog/resources?type=…
 *
 * The type buttons filter this same section in place. "View details" opens the
 * material's own page at /materials/:type/:idOrSlug.
 */
export function MaterialsSection({limit = 8}) {
  const navigate = useNavigate();
  const {cart, addToCart} = useCart();
  const {
    publicResources,
    publicResourcesLoading,
    publicResourcesError,
    fetchPublicResources,
  } = useUserPublicCatalog();

  const [activeType, setActiveType] = useState("");

  useEffect(() => {
    fetchPublicResources({
      page: 1,
      limit,
      sort: "newest",
      ...(activeType ? {type: activeType} : {}),
    });
  }, [activeType, fetchPublicResources, limit]);

  const materials = useMemo(
    () => (Array.isArray(publicResources) ? publicResources : []).map(mapPublicResource),
    [publicResources]
  );

  const openDetail = (material) => {
    const path = materialDetailPath(material);
    if (path) navigate(path);
  };

  return (
    <section
      id="materials"
      className="border-t border-slate-100 bg-slate-50 px-4 py-14 sm:py-16"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-2">
          <div className="max-w-2xl">
            <span className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
              Study materials
            </span>
            <h2 className="mt-3 text-2xl tracking-tight text-slate-900 sm:text-3xl">
              Books, journals, seminars &amp; more
            </h2>
            <p className="mt-2 text-sm text-slate-600 sm:text-base">
              Pick a material type to see what is available — the results update right here.
            </p>
          </div>
        </div>

        {/* Type buttons — each one filters this section in place. */}
        <div className="mb-5 flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const isActive = activeType === tab.id;
            return (
              <button
                key={tab.id || "all"}
                type="button"
                aria-pressed={isActive}
                onClick={() => setActiveType(tab.id)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                  isActive
                    ? "border-blue-900 bg-blue-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {publicResourcesError ? (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="m-0 flex-1 text-sm text-amber-800">{publicResourcesError}</p>
            <button
              type="button"
              onClick={() =>
                fetchPublicResources({
                  page: 1,
                  limit,
                  sort: "newest",
                  ...(activeType ? {type: activeType} : {}),
                })
              }
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : null}

        {publicResourcesLoading ? (
          <CardGridSkeleton count={Math.min(limit, 4)} />
        ) : materials.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {materials.slice(0, limit).map((material) => {
              const type = cartMaterialType(material.type);
              const inCart = cart.some((item) =>
                String(item.id) === String(material.id) && sameCartType(item.type, type)
              );
              return (
                <ResourceCard
                  key={material.id}
                  image={material.image}
                  title={material.title}
                  subtitle={material.subtitle}
                  badge={material.badge}
                  meta={material.meta}
                  price={material.price}
                  isFree={material.isFree}
                  cornerClass="rounded-md"
                  compact
                  onOpen={() => openDetail(material)}
                  primaryLabel="View details"
                  onPrimary={() => openDetail(material)}
                  secondaryLabel={material.isFree ? undefined : inCart ? "In cart" : "Add to cart"}
                  secondaryDisabled={inCart}
                  onSecondary={() => {
                    if (inCart || material.isFree || !material.id || !type) return;
                    addToCart({
                      id: material.id,
                      type,
                      title: material.title,
                      instructor: material.resourcePerson?.name || material.subtitle || "",
                      thumbnail: material.image || "/images/course_placeholder.jpg",
                      category: material.badge,
                      level: material.level || "",
                      price: material.price,
                    });
                    window.dispatchEvent(new CustomEvent(CART_NOTIFY_EVENT, {
                      detail: {message: `Added to cart · ${material.title}`},
                    }));
                  }}
                />
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-slate-500">
            No {activeType ? `${materialTypeLabel(activeType).toLowerCase()}s` : "materials"} available right now.
          </div>
        )}
      </div>
    </section>
  );
}

export default MaterialsSection;
